/**
 * HUD - In-game heads-up display showing score, timer, stamina, possession,
 * shots on target, fatigue indicator, and control hints.
 * Covers tasks 3.1 (more stats), 3.9 (scoreboard animations), 3.12 (timer),
 * 3.13 (possession %), 3.14 (shots on target), 3.15 (fatigue indicator),
 * 7.4 (control hints overlay).
 */

import * as GUI from "babylonjs-gui";
import { GameState } from "../systems/GameState";

export class HUD {
  private _texture: GUI.AdvancedDynamicTexture;
  private _scoreText: GUI.TextBlock;
  private _timerText: GUI.TextBlock;
  private _staminaBar: GUI.Rectangle;
  private _staminaFill: GUI.Rectangle;
  private _powerBar: GUI.Rectangle;
  private _powerFill: GUI.Rectangle;
  private _possessionBar: GUI.Rectangle;
  private _possessionFill: GUI.Rectangle;
  private _possessionText: GUI.TextBlock;
  private _shotsText: GUI.TextBlock;
  private _fatigueLabel: GUI.TextBlock;
  private _controlsHint: GUI.TextBlock;
  private _scoreContainer: GUI.Rectangle;
  private _halfLabel: GUI.TextBlock;
  private _extraTimeLabel: GUI.TextBlock;
  private _autoPassLabel: GUI.TextBlock;

  // Score animation
  private _prevPlayerScore = 0;
  private _prevOpponentScore = 0;
  private _scoreFlashTimer = 0;

  constructor(private _gameState: GameState) {
    this._texture = GUI.AdvancedDynamicTexture.CreateFullscreenUI("hud");

    // --- Scorebug Container (Top Center) ---
    this._scoreContainer = new GUI.Rectangle("scorebug");
    this._scoreContainer.width = "340px";
    this._scoreContainer.height = "54px";
    this._scoreContainer.thickness = 0;
    this._scoreContainer.background = "rgba(0,0,0,0.65)";
    this._scoreContainer.cornerRadius = 6;
    this._scoreContainer.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_TOP;
    this._scoreContainer.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
    this._scoreContainer.top = "18px";
    this._texture.addControl(this._scoreContainer);

    // Left team color bar (Player - Blue)
    const playerBar = new GUI.Rectangle("playerBar");
    playerBar.width = "8px";
    playerBar.height = "100%";
    playerBar.thickness = 0;
    playerBar.background = "#1155cc";
    playerBar.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    this._scoreContainer.addControl(playerBar);

    // Right team color bar (Opponent - Red)
    const opponentBar = new GUI.Rectangle("opponentBar");
    opponentBar.width = "8px";
    opponentBar.height = "100%";
    opponentBar.thickness = 0;
    opponentBar.background = "#ff3366";
    opponentBar.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_RIGHT;
    this._scoreContainer.addControl(opponentBar);

    // Score Text
    this._scoreText = new GUI.TextBlock("score", "0 - 0");
    this._scoreText.color = "white";
    this._scoreText.fontSize = 28;
    this._scoreText.fontWeight = "900";
    this._scoreText.fontFamily = "Orbitron, sans-serif";
    this._scoreText.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
    this._scoreContainer.addControl(this._scoreText);

    // Team Labels
    const playerLabel = new GUI.TextBlock("playerLabel", "YOU");
    playerLabel.color = "#88aaff";
    playerLabel.fontSize = 11;
    playerLabel.fontWeight = "700";
    playerLabel.fontFamily = "Orbitron, sans-serif";
    playerLabel.left = "20px";
    playerLabel.width = "80px";
    playerLabel.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    this._scoreContainer.addControl(playerLabel);

    const opponentLabel = new GUI.TextBlock("opponentLabel", "CPU");
    opponentLabel.color = "#ff8888";
    opponentLabel.fontSize = 11;
    opponentLabel.fontWeight = "700";
    opponentLabel.fontFamily = "Orbitron, sans-serif";
    opponentLabel.left = "-20px";
    opponentLabel.width = "80px";
    opponentLabel.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_RIGHT;
    this._scoreContainer.addControl(opponentLabel);

    // Half label
    this._halfLabel = new GUI.TextBlock("halfLabel", "1ST");
    this._halfLabel.color = "#aaaaaa";
    this._halfLabel.fontSize = 9;
    this._halfLabel.fontWeight = "700";
    this._halfLabel.fontFamily = "Orbitron, sans-serif";
    this._halfLabel.top = "-16px";
    this._halfLabel.width = "50px";
    this._halfLabel.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
    this._scoreContainer.addControl(this._halfLabel);

    // Timer Container (Below Scorebug)
    const timerBox = new GUI.Rectangle("timerBox");
    timerBox.width = "120px";
    timerBox.height = "30px";
    timerBox.thickness = 0;
    timerBox.background = "rgba(0,0,0,0.8)";
    timerBox.cornerRadius = 3;
    timerBox.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_TOP;
    timerBox.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
    timerBox.top = "74px";
    this._texture.addControl(timerBox);

    this._timerText = new GUI.TextBlock("timer", "03:00");
    this._timerText.color = "#00ff88";
    this._timerText.fontSize = 18;
    this._timerText.fontWeight = "700";
    this._timerText.fontFamily = "Orbitron, sans-serif";
    timerBox.addControl(this._timerText);

    // Extra time label
    this._extraTimeLabel = new GUI.TextBlock("extraTimeLabel", "EXTRA TIME");
    this._extraTimeLabel.color = "#ff9900";
    this._extraTimeLabel.fontSize = 10;
    this._extraTimeLabel.fontWeight = "700";
    this._extraTimeLabel.fontFamily = "Orbitron, sans-serif";
    this._extraTimeLabel.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_TOP;
    this._extraTimeLabel.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
    this._extraTimeLabel.top = "104px";
    this._extraTimeLabel.isVisible = false;
    this._texture.addControl(this._extraTimeLabel);

    // --- Status Bars (Bottom Left) ---
    const statusContainer = new GUI.StackPanel("statusContainer");
    statusContainer.width = "220px";
    statusContainer.height = "130px";
    statusContainer.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    statusContainer.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_BOTTOM;
    statusContainer.left = "20px";
    statusContainer.top = "-20px";
    this._texture.addControl(statusContainer);

    // Stamina Label
    const staminaLabel = new GUI.TextBlock("staminaLabel", "STAMINA");
    staminaLabel.color = "white";
    staminaLabel.fontSize = 10;
    staminaLabel.fontWeight = "700";
    staminaLabel.fontFamily = "Orbitron, sans-serif";
    staminaLabel.height = "18px";
    staminaLabel.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    statusContainer.addControl(staminaLabel);

    // Stamina bar background
    this._staminaBar = new GUI.Rectangle("staminaBar");
    this._staminaBar.width = "200px";
    this._staminaBar.height = "8px";
    this._staminaBar.cornerRadius = 2;
    this._staminaBar.color = "transparent";
    this._staminaBar.thickness = 0;
    this._staminaBar.background = "rgba(255,255,255,0.1)";
    this._staminaBar.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    statusContainer.addControl(this._staminaBar);

    // Stamina fill
    this._staminaFill = new GUI.Rectangle("staminaFill");
    this._staminaFill.width = "200px";
    this._staminaFill.height = "100%";
    this._staminaFill.cornerRadius = 2;
    this._staminaFill.color = "transparent";
    this._staminaFill.thickness = 0;
    this._staminaFill.background = "#00ff88";
    this._staminaFill.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    this._staminaBar.addControl(this._staminaFill);

    // Fatigue indicator (task 3.15)
    this._fatigueLabel = new GUI.TextBlock("fatigueLabel", "⚠ FATIGUED");
    this._fatigueLabel.color = "#ff3366";
    this._fatigueLabel.fontSize = 10;
    this._fatigueLabel.fontWeight = "700";
    this._fatigueLabel.fontFamily = "Orbitron, sans-serif";
    this._fatigueLabel.height = "16px";
    this._fatigueLabel.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    this._fatigueLabel.isVisible = false;
    statusContainer.addControl(this._fatigueLabel);

    // Spacer
    const barSpacer = new GUI.Rectangle("barSpacer");
    barSpacer.height = "10px";
    barSpacer.thickness = 0;
    statusContainer.addControl(barSpacer);

    // ⚡ Kick power bar
    this._powerBar = new GUI.Rectangle("powerBar");
    this._powerBar.width = "200px";
    this._powerBar.height = "8px";
    this._powerBar.cornerRadius = 2;
    this._powerBar.color = "transparent";
    this._powerBar.thickness = 0;
    this._powerBar.background = "rgba(255,255,255,0.1)";
    this._powerBar.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    this._powerBar.isVisible = false;
    statusContainer.addControl(this._powerBar);

    this._powerFill = new GUI.Rectangle("powerFill");
    this._powerFill.width = "0px";
    this._powerFill.height = "100%";
    this._powerFill.cornerRadius = 2;
    this._powerFill.color = "transparent";
    this._powerFill.thickness = 0;
    this._powerFill.background = "#ffcc00";
    this._powerFill.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    this._powerBar.addControl(this._powerFill);

    const powerLabel = new GUI.TextBlock("powerLabel", "POWER");
    powerLabel.color = "#ffcc00";
    powerLabel.fontSize = 10;
    powerLabel.fontWeight = "700";
    powerLabel.fontFamily = "Orbitron, sans-serif";
    powerLabel.height = "16px";
    powerLabel.top = "-22px";
    powerLabel.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    this._powerBar.addControl(powerLabel);

    // Auto-pass label
    this._autoPassLabel = new GUI.TextBlock("autoPassLabel", "AUTO-PASS ON");
    this._autoPassLabel.color = "#00ffcc";
    this._autoPassLabel.fontSize = 10;
    this._autoPassLabel.fontWeight = "700";
    this._autoPassLabel.fontFamily = "Orbitron, sans-serif";
    this._autoPassLabel.height = "16px";
    this._autoPassLabel.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    this._autoPassLabel.isVisible = false;
    statusContainer.addControl(this._autoPassLabel);

    // --- Possession Bar (Bottom Center) - task 3.13 ---
    const possessionContainer = new GUI.Rectangle("possessionContainer");
    possessionContainer.width = "280px";
    possessionContainer.height = "32px";
    possessionContainer.thickness = 0;
    possessionContainer.background = "transparent";
    possessionContainer.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
    possessionContainer.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_BOTTOM;
    possessionContainer.top = "-20px";
    this._texture.addControl(possessionContainer);

    const possLabel = new GUI.TextBlock("possLabel", "POSSESSION");
    possLabel.color = "#aaaaaa";
    possLabel.fontSize = 9;
    possLabel.fontWeight = "700";
    possLabel.fontFamily = "Orbitron, sans-serif";
    possLabel.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_TOP;
    possLabel.top = "0px";
    possessionContainer.addControl(possLabel);

    this._possessionBar = new GUI.Rectangle("possessionBar");
    this._possessionBar.width = "280px";
    this._possessionBar.height = "8px";
    this._possessionBar.cornerRadius = 2;
    this._possessionBar.thickness = 0;
    this._possessionBar.background = "#ff3366";
    this._possessionBar.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_BOTTOM;
    this._possessionBar.top = "-2px";
    possessionContainer.addControl(this._possessionBar);

    this._possessionFill = new GUI.Rectangle("possessionFill");
    this._possessionFill.width = "50%";
    this._possessionFill.height = "100%";
    this._possessionFill.cornerRadius = 2;
    this._possessionFill.thickness = 0;
    this._possessionFill.background = "#1155cc";
    this._possessionFill.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    this._possessionBar.addControl(this._possessionFill);

    this._possessionText = new GUI.TextBlock("possessionText", "50%  •  50%");
    this._possessionText.color = "rgba(255,255,255,0.6)";
    this._possessionText.fontSize = 9;
    this._possessionText.fontFamily = "Orbitron, sans-serif";
    this._possessionText.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_BOTTOM;
    this._possessionText.top = "0px";
    possessionContainer.addControl(this._possessionText);

    // --- Shots on Target (Bottom Right) - task 3.14 ---
    this._shotsText = new GUI.TextBlock("shotsText", "SHOTS: 0 - 0");
    this._shotsText.color = "rgba(255,255,255,0.5)";
    this._shotsText.fontSize = 10;
    this._shotsText.fontWeight = "700";
    this._shotsText.fontFamily = "Orbitron, sans-serif";
    this._shotsText.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_RIGHT;
    this._shotsText.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_BOTTOM;
    this._shotsText.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_RIGHT;
    this._shotsText.left = "-20px";
    this._shotsText.top = "-58px";
    this._texture.addControl(this._shotsText);

    // Controls legend (bottom-right) - task 7.4
    this._controlsHint = new GUI.TextBlock(
      "controls",
      "SPACE SHOOT  •  E THROUGH  •  Q LOB  •  F TACKLE  •  R AUTO-PASS"
    );
    this._controlsHint.color = "rgba(255,255,255,0.4)";
    this._controlsHint.fontSize = 9;
    this._controlsHint.fontWeight = "700";
    this._controlsHint.fontFamily = "Orbitron, sans-serif";
    this._controlsHint.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_RIGHT;
    this._controlsHint.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_BOTTOM;
    this._controlsHint.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_RIGHT;
    this._controlsHint.left = "-20px";
    this._controlsHint.top = "-44px";
    this._texture.addControl(this._controlsHint);
  }

  /**
   * Update HUD each frame.
   * @param staminaPercent 0-100
   * @param kickCharge 0-1
   * @param playerPossession 0-100
   * @param playerShots shots on target
   * @param opponentShots opponent shots on target
   * @param isAutoPassOn auto-pass state
   */
  update(
    staminaPercent: number,
    kickCharge: number,
    playerPossession: number = 50,
    playerShots: number = 0,
    opponentShots: number = 0,
    isAutoPassOn: boolean = false
  ): void {
    const s = this._gameState.score;

    // Score animation (task 3.9)
    if (s.player !== this._prevPlayerScore || s.opponent !== this._prevOpponentScore) {
      this._prevPlayerScore = s.player;
      this._prevOpponentScore = s.opponent;
      this._scoreFlashTimer = 1.0;
    }

    if (this._scoreFlashTimer > 0) {
      this._scoreFlashTimer -= 0.016;
      const pulse = Math.sin(this._scoreFlashTimer * 20) * 0.5 + 0.5;
      this._scoreContainer.scaleX = 1 + pulse * 0.05;
      this._scoreContainer.scaleY = 1 + pulse * 0.05;
    } else {
      this._scoreContainer.scaleX = 1;
      this._scoreContainer.scaleY = 1;
    }

    this._scoreText.text = `${s.player} - ${s.opponent}`;

    // Timer
    const t = this._gameState.timeRemaining;
    const min = Math.floor(t / 60);
    const sec = Math.floor(t % 60);
    this._timerText.text = `${min}:${sec.toString().padStart(2, "0")}`;

    // Color timer red when < 30s
    if (t <= 30) {
      this._timerText.color = "#ff3366";
    } else if (t <= 60) {
      this._timerText.color = "#ffcc00";
    } else {
      this._timerText.color = "#00ff88";
    }

    // Half indicator
    this._halfLabel.text = this._gameState.inExtraTime ? "ET" :
      (this._gameState.isFirstHalf ? "1ST" : "2ND");
    this._extraTimeLabel.isVisible = this._gameState.inExtraTime;

    // Stamina bar
    const fillWidth = Math.max(0, (staminaPercent / 100) * 200);
    this._staminaFill.width = `${fillWidth}px`;
    if (staminaPercent > 50) {
      this._staminaFill.background = "#00ff88";
    } else if (staminaPercent > 25) {
      this._staminaFill.background = "#ffcc00";
    } else {
      this._staminaFill.background = "#ff3366";
    }

    // Fatigue indicator (task 3.15)
    this._fatigueLabel.isVisible = staminaPercent <= 5;

    // Power bar
    if (kickCharge > 0) {
      this._powerBar.isVisible = true;
      const pw = Math.max(0, kickCharge * 200);
      this._powerFill.width = `${pw}px`;
      if (kickCharge < 0.4) {
        this._powerFill.background = "#00ff88";
      } else if (kickCharge < 0.75) {
        this._powerFill.background = "#ffcc00";
      } else {
        this._powerFill.background = "#ff3366";
      }
    } else {
      this._powerBar.isVisible = false;
    }

    // Possession bar (task 3.13)
    const pct = Math.max(0, Math.min(100, playerPossession));
    this._possessionFill.width = `${pct}%`;
    this._possessionText.text = `${pct}%  •  ${100 - pct}%`;

    // Shots on target (task 3.14)
    this._shotsText.text = `SHOTS ON TARGET: ${playerShots} - ${opponentShots}`;

    // Auto-pass label
    this._autoPassLabel.isVisible = isAutoPassOn;
  }

  setControlsHintVisible(visible: boolean): void {
    this._controlsHint.isVisible = visible;
  }

  setPossessionVisible(visible: boolean): void {
    // possession is part of the status container — toggle via texture
  }

  setVisible(visible: boolean): void {
    this._texture.rootContainer.isVisible = visible;
  }

  dispose(): void {
    this._texture.dispose();
  }
}
